import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, Tag, Loader2 } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, affiliateCode, setAffiliate } = useCart();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState(affiliateCode || "");
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeValid, setCodeValid] = useState(!!affiliateCode);
  const [checkingOut, setCheckingOut] = useState(false);

  const validateCode = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingCode(true);
    try {
      const response = await axios.get(`${API}/affiliate/validate/${promoCode}`);
      if (response.data.valid) {
        setAffiliate(promoCode);
        setCodeValid(true);
        toast.success(`Code applied! Referred by ${response.data.affiliate_name}`);
      } else {
        setCodeValid(false);
        toast.error("Invalid affiliate code");
      }
    } catch (error) {
      setCodeValid(false);
      toast.error("Invalid affiliate code");
    } finally {
      setValidatingCode(false);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Please sign in to checkout");
      navigate("/login");
      return;
    }

    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setCheckingOut(true);
    try {
      const response = await axios.post(
        `${API}/checkout/create-session`,
        {
          origin_url: window.location.origin,
          affiliate_code: codeValid ? promoCode : null
        },
        { headers: getAuthHeaders() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to create checkout session");
      setCheckingOut(false);
    }
  };

  if (!user) {
    return (
      <div className="container-main section-spacing text-center">
        <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
        <h1 className="text-h2 mb-4">Sign In to View Cart</h1>
        <p className="text-muted-foreground mb-8">
          Please sign in to add items to your cart and checkout.
        </p>
        <Link to="/login">
          <Button className="btn-primary" data-testid="login-to-cart-btn">
            Sign In
          </Button>
        </Link>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="container-main section-spacing text-center">
        <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
        <h1 className="text-h2 mb-4">Your Cart is Empty</h1>
        <p className="text-muted-foreground mb-8">
          Looks like you haven't added any products yet.
        </p>
        <Link to="/#products">
          <Button className="btn-primary" data-testid="browse-products-btn">
            Browse Products
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container-main section-spacing">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-h2 mb-8">Shopping Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((item) => (
            <div 
              key={item.product_id} 
              className="flex gap-6 p-6 bg-white border border-border rounded-xl"
              data-testid={`cart-item-${item.product_id}`}
            >
              <Link to={`/products/${item.product_id}`}>
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded-lg"
                />
              </Link>
              <div className="flex-1">
                <Link to={`/products/${item.product_id}`}>
                  <h3 className="font-heading text-lg hover:text-primary transition-colors">
                    {item.name}
                  </h3>
                </Link>
                <p className="text-primary font-medium text-lg mt-1">
                  ${item.price.toFixed(2)}
                </p>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      data-testid={`decrease-qty-${item.product_id}`}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      data-testid={`increase-qty-${item.product_id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeFromCart(item.product_id)}
                    data-testid={`remove-item-${item.product_id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
              <div className="text-right">
                <p className="font-heading text-xl text-primary">
                  ${(item.price * item.quantity).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-border rounded-xl p-6 sticky top-24">
            <h2 className="font-heading text-xl mb-6">Order Summary</h2>

            {/* Affiliate Code */}
            <div className="mb-6">
              <label className="text-sm font-medium mb-2 block">Affiliate Code</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className={codeValid ? "border-green-500" : ""}
                  data-testid="affiliate-code-input"
                />
                <Button 
                  variant="outline" 
                  onClick={validateCode}
                  disabled={validatingCode}
                  data-testid="apply-code-btn"
                >
                  {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
              {codeValid && (
                <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Affiliate code applied
                </p>
              )}
            </div>

            <div className="space-y-3 border-t pt-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${cart.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-green-600">Free</span>
              </div>
              <div className="flex justify-between text-xl font-heading border-t pt-4">
                <span>Total</span>
                <span className="text-primary">${cart.total.toFixed(2)}</span>
              </div>
            </div>

            <Button 
              className="btn-primary w-full mt-6"
              onClick={handleCheckout}
              disabled={checkingOut}
              data-testid="checkout-btn"
            >
              {checkingOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Proceed to Checkout"
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center mt-4">
              Secure checkout powered by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
